/**
 * Meta 信息管理工具函数
 * 用于统一管理页面的 title、keywords、description
 */

/**
 * 设置页面 Meta 信息
 * @param {Object} meta - Meta 信息对象
 * @param {string} meta.title - 页面标题（会自动添加网站名称后缀）
 * @param {string} meta.keywords - 关键词
 * @param {string} meta.description - 页面描述
 * @param {string} meta.siteName - 网站名称（默认：亚科股份）
 * @returns {Object} 格式化后的 meta 信息
 */
export function setPageMeta(meta = {}) {
  const siteName = meta.siteName || '亚科股份'
  const defaultMeta = {
    title: siteName,
    keywords: siteName,
    description: siteName
  }

  const finalMeta = {
    ...defaultMeta,
    ...meta,
    // 确保 title 包含网站名称（如果还没有）
    title: meta.title
      ? (meta.title.includes(siteName) ? meta.title : `${meta.title}_${siteName}`)
      : defaultMeta.title
  }
  return finalMeta
}

/**
 * 从 API 响应中提取 Meta 信息
 * @param {Object} data - API 返回的数据对象
 * @param {Object} options - 配置选项
 * @param {string} options.titleKey - title 字段名（默认：'seoTitle' 或 'title'）
 * @param {string} options.keywordsKey - keywords 字段名（默认：'keywords'）
 * @param {string} options.descriptionKey - description 字段名（默认：'description'）
 * @param {string} options.fallbackTitle - 默认标题
 * @returns {Object} Meta 信息对象
 */
export function extractMetaFromData(data = {}, options = {}) {
  const {
    titleKey = 'seoTitle',
    keywordsKey = 'keywords',
    descriptionKey = 'description',
    fallbackTitle = ''
  } = options

  return {
    title: data[titleKey] || data.title || fallbackTitle,
    keywords: data[keywordsKey] || '',
    description: data[descriptionKey] || data.summary || ''
  }
}

/**
 * 从缓存的 menuItems 中获取 Meta 信息
 * @param {string} routePath - 当前路由路径（包含语言前缀，如 /zh/products）
 * @param {string} currentLanguage - 当前语言代码（如 'zh', 'en'）
 * @returns {Object|null} Meta 信息对象，如果未找到则返回 null
 */
export function getMetaFromMenuItems(routePath, currentLanguage = 'zh') {
  try {
    const menuItemsStr = typeof localStorage !== 'undefined' ? localStorage.getItem('menuItems') : null
    if (!menuItemsStr) {
      return null
    }
    
    const menuItems = JSON.parse(menuItemsStr)
    if (!Array.isArray(menuItems)) {
      return null
    }

    // 获取当前路由路径（去除语言前缀）
    const pathWithoutLang = routePath.replace(/^\/(zh|en|ja|ko|ru|es|fr|ar)/, '') || routePath
    const normalizedPath = pathWithoutLang.startsWith('/') ? pathWithoutLang : `/${pathWithoutLang}`
    // 先尝试在一级菜单中查找
    let matchedItem = menuItems.find(item => {
      // 如果菜单项有 lang 字段，需要匹配当前语言
      if (item.lang && item.lang !== currentLanguage) {
        return false
      }
      
      // 匹配 path 或 url
      const itemPath = item.path || ''
      const itemUrl = item.url || ''
      
      return itemPath === normalizedPath || 
             itemPath === pathWithoutLang ||
             itemUrl === pathWithoutLang.replace(/^\//, '') ||
             itemUrl === pathWithoutLang
    })

    // 如果在一级菜单中找到，返回其 meta 信息
    if (matchedItem && (matchedItem.seoTitle || matchedItem.keywords || matchedItem.description)) {
      return {
        title: matchedItem.seoTitle || '',
        keywords: matchedItem.keywords || '',
        description: matchedItem.description || ''
      }
    }

    // 如果没找到，尝试在二级菜单（children）中查找
    for (const item of menuItems) {
      if (!item.children || !Array.isArray(item.children)) {
        continue
      }

      // 检查语言匹配
      if (item.lang && item.lang !== currentLanguage) {
        continue
      }

      // 在 children 中查找匹配的项
      const matchedChild = item.children.find(child => {
        const childPath = child.path || ''
        const childUrl = child.url || ''
        
        return childPath === normalizedPath || 
               childPath === pathWithoutLang ||
               childUrl === pathWithoutLang.replace(/^\//, '') ||
               childUrl === pathWithoutLang
      })

      if (matchedChild && (matchedChild.seoTitle || matchedChild.keywords || matchedChild.description)) {
        return {
          title: matchedChild.seoTitle || '',
          keywords: matchedChild.keywords || '',
          description: matchedChild.description || ''
        }
      }
    }

    return null
  } catch (error) {
    console.error('从 menuItems 获取 Meta 信息失败:', error)
    return null
  }
}

/**
 * 根据路由路径获取默认 Meta 信息
 * @param {string} path - 路由路径
 * @returns {Object} Meta 信息对象
 */
export function getDefaultMetaByRoute(path) {
  const metaMap = {
    '/': {
      title: '生物缓冲剂_体外诊断原辅料_医药中间体_新能源功能材料_亚科股份',
      keywords: '生物缓冲剂、体外诊断原辅料、医药中间体、新能源功能材料、生物试剂、化学试剂',
      description: '亚科股份现已形成集研发、生产、销售和服务于一体的国家高新技术企业。业务范围覆盖生物缓冲剂、体外诊断试剂原辅料、医药中间体及药用辅料、新能源功能材料等。'
    },
    '/products': {
      title: '产品中心_生命科学产品_功能性新材料_新能源锂电材料_亚科股份',
      keywords: '产品中心、生命科学产品、功能性新材料、新能源锂电材料',
      description: '亚科股份产品中心，提供生命科学产品、功能性新材料、新能源锂电材料等产品。'
    },
    '/about-us/company': {
      title: '关于我们_公司介绍_亚科股份',
      keywords: '关于我们、公司介绍、亚科股份',
      description: '亚科股份公司介绍，了解我们的发展历程、企业文化等。'
    },
    '/information-center': {
      title: '资讯中心_新闻动态_亚科股份',
      keywords: '资讯中心、新闻动态、行业资讯',
      description: '亚科股份资讯中心，了解最新的行业资讯和公司动态。'
    }
  }

  // 精确匹配
  if (metaMap[path]) {
    return metaMap[path]
  }

  // 路径前缀匹配
  for (const [routePath, meta] of Object.entries(metaMap)) {
    if (path.startsWith(routePath)) {
      return meta
    }
  }

  // 默认值
  return {
    title: '亚科股份',
    keywords: '亚科股份',
    description: '亚科股份'
  }
}

/**
 * 设置页面 Meta 信息（Vuex action 的便捷方法）
 * 需要在组件中使用：this.$meta.setMeta({...})
 * 或通过 Vue 插件注册后使用
 */
export const metaMixin = {
  methods: {
    /**
     * 设置页面 Meta 信息
     * @param {Object} meta - Meta 信息对象
     */
    $setMeta(meta) {
      if (this.$store) {
        const formattedMeta = setPageMeta(meta)
        this.$store.dispatch('setMetaInfo', formattedMeta)
      } else {
        console.warn('$setMeta: Store 不可用')
      }
    },

    /**
     * 从 API 数据中设置 Meta 信息
     * @param {Object} data - API 返回的数据
     * @param {Object} options - 配置选项
     */
    $setMetaFromData(data, options = {}) {
      const meta = extractMetaFromData(data, options)
      this.$setMeta(meta)
    },

    /**
     * 根据路由设置默认 Meta 信息
     */
    $setMetaByRoute() {
      if (this.$route && this.$store) {
        const meta = getDefaultMetaByRoute(this.$route.path)
        this.$setMeta(meta)
      }
    },

    /**
     * 从缓存的 menuItems 中设置 Meta 信息
     * @param {string} currentLanguage - 当前语言代码（可选，默认从 store 获取）
     */
    $setMetaFromMenuItems(currentLanguage) {
      if (!this.$route || !this.$store) {
        return
      }

      const lang = currentLanguage || (this.$store.state.app && this.$store.state.app.language) || 'zh'
      const meta = getMetaFromMenuItems(this.$route.path, lang)
      if (meta && (meta.title || meta.keywords || meta.description)) {
        console.log('从 menuItems 中获取 Meta 信息2222:', meta)
        this.$setMeta(meta)
      } else {
        // 如果从 menuItems 中没找到，使用默认的 route meta
        this.$setMetaByRoute()
      }
    }
  }
}

/**
 * Vue 插件：注册全局 Meta 工具方法
 * 在 main.js 中使用：Vue.use(metaPlugin)
 */
export const metaPlugin = {
  install(Vue) {
    // 注册全局方法
    Vue.prototype.$setMeta = function(meta) {
      if (this.$store) {
        const formattedMeta = setPageMeta(meta)
        this.$store.dispatch('setMetaInfo', formattedMeta)
      }
    }

    Vue.prototype.$setMetaFromData = function(data, options = {}) {
      const meta = extractMetaFromData(data, options)
      this.$setMeta(meta)
    }

    Vue.prototype.$setMetaByRoute = function() {
      if (this.$route && this.$store) {
        const meta = getDefaultMetaByRoute(this.$route.path)
        this.$setMeta(meta)
      }
    }

    Vue.prototype.$setMetaFromMenuItems = function(currentLanguage) {
      if (!this.$route || !this.$store) {
        return
      }

      const lang = currentLanguage || (this.$store.state.app && this.$store.state.app.language) || 'zh'
      const meta = getMetaFromMenuItems(this.$route.path, lang)
      
      if (meta && (meta.title || meta.keywords || meta.description)) {
        this.$setMeta(meta)
      } else {
        // 如果从 menuItems 中没找到，使用默认的 route meta
        this.$setMetaByRoute()
      }
    }
  }
}

// 默认导出所有工具函数
export default {
  setPageMeta,
  extractMetaFromData,
  getMetaFromMenuItems,
  getDefaultMetaByRoute,
  metaMixin,
  metaPlugin
}

